import { FirestoreDatabase as Database, FirestoreCollectionReference, FirestoreDocumentReference, FirestoreDocumentSnapshot, FirestoreQuery, FirestoreQuerySnapshot, FirestoreTimestamp, FirestoreTimestampServerValue } from '../db/common';
import { ConsoleLogger, ILogger, NoOpLogger } from '../utils/logger';
import { WithID } from '../db';

/**
 * Abstract Base Service Class for Firestore
 * All Firestore services MUST extend this class and implement CRUD operations
 * 
 * This is the Firestore equivalent of DatabaseService (Realtime Database)
 * 
 * @template T - The document type this service manages
 * @template TCreate - Optional: The type used for creating documents (defaults to T)
 * @template TUpdate - Optional: The type used for updating documents (defaults to Partial<T>)
 */
abstract class FirestoreService<T, TCreate = T, TUpdate = Partial<T>> {
    protected logger: ILogger;

    constructor(
        protected firestore: Database,
        logger?: ILogger | boolean
    ) {
        // Use provided logger or default to NoOpLogger (does nothing)
        this.logger = !logger ?
            new NoOpLogger()
            : ((logger === true) || this.shouldLog()) ?
                new ConsoleLogger()
                : logger;
    }

    /**
     * Get the collection path for this document type
     * Example: 'users', 'listings'
     */
    protected abstract getCollectionPath(): string;

    /**
     * Get a reference to the collection
     * Changed to public instead of protected, only to be used directly for .onSnapshot query listener on docs
     */
    public getCollection(): FirestoreCollectionReference {
        return this.firestore.collection(this.getCollectionPath());
    }

    /**
     * Get a reference to a specific document by ID
     * Changed to public instead of protected, only to be used directly for firestore().runTransaction listener on docs
     */
    public getDocumentRef(id?: string): FirestoreDocumentReference {
        if (!!id)
            return this.getCollection().doc(id);
        else
            return this.getCollection().doc();
    }

    /**
     * Convert Firestore document to typed entity
     * Changed to public static instead of protected to be used everywhere
     */
    public static documentToEntity<T>(doc: FirestoreDocumentSnapshot): WithID<T> | null {
        if (!doc.exists) {
            return null;
        }
        return { ...doc.data() as T, id: doc.id };
    }

    /**
     * Convert Firestore QuerySnapshot to array of entities
     * Changed to public static instead of protected to be used everywhere
     */
    public static querySnapshotToEntities<T>(snapshot: FirestoreQuerySnapshot): WithID<T>[] {
        return snapshot.docs.map(doc => this.documentToEntity<T>(doc)!).filter(Boolean);
    }

    // ============================================
    // LOGGING HELPER METHODS
    // ============================================

    /**
       * Get the service name for logging
       * Override this to customize log messages
       */
    protected getServiceName(): string {
        return this.constructor.name;
    }

    /**
     * Enable/disable logging for this service
     * Override to disable logging in production, etc.
     */
    protected shouldLog(): boolean {
        return true;
    }

    /**
     * Log the start of an operation (debug level)
     */
    protected logOperation(operation: string, id: string, data?: any): void {
        if (!this.shouldLog()) return;

        const message = `${this.getServiceName()}.${operation}`;
        this.logger.debug(message, {
            id,
            path: this.getCollectionPath(),
            data
        });
    }

    /**
     * Log successful completion of an operation
     */
    protected logSuccess(operation: string, id: string, result?: any): void {
        if (!this.shouldLog()) return;

        const message = `${this.getServiceName()}.${operation} succeeded`;
        this.logger.log(message, {
            id,
            path: this.getCollectionPath(),
            result
        });
    }

    /**
     * Log an error during an operation
     */
    protected logError(operation: string, id: string, error: Error, data?: any): void {
        if (!this.shouldLog()) return;

        const message = `${this.getServiceName()}.${operation} failed`;
        this.logger.error(message, {
            id,
            path: this.getCollectionPath(),
            error: error.message,
            stack: error.stack,
            data,
        });
    }

    // ============================================
    // ABSTRACT METHODS - MUST BE IMPLEMENTED
    // ============================================

    /**
     * Get a document by ID
     * @param id - The document ID
     * @returns The document or null if not found
     */
    abstract get(id: string): Promise<T | null>;

    /**
     * Create a new document
     * @param data - The data to create
     * @param id - Optional: Specific ID to use (otherwise auto-generated)
     * @returns The ID of the created document
     */
    abstract create(data: TCreate, id?: string): Promise<string>;

    /**
     * Update a document
     * @param id - The document ID
     * @param data - The data to update
     */
    abstract update(id: string, data: TUpdate): Promise<void>;

    /**
     * Delete a document
     * @param id - The document ID
     */
    abstract delete(id: string): Promise<void>;

    /**
     * Merge the information in the document
     * Difference with update: if the document does not exist it will create it instead of failing
     * @param id - The document ID
     * @param data - The data to merge in the document
     */
    abstract merge(id: string, data: TUpdate): Promise<void>;

    // ============================================
    // OPTIONAL METHODS - CAN BE OVERRIDDEN
    // ============================================

    /**
     * List all documents (WARNING: can be expensive on large collections!)
     */
    async list(): Promise<WithID<T>[]> {
        this.logOperation('list', 'all');

        try {
            const snapshot = await this.getCollection().get();
            const result = FirestoreService.querySnapshotToEntities<T>(snapshot);

            this.logSuccess('list', 'all', { count: result.length });
            return result;
        } catch (error) {
            this.logError('list', 'all', error as Error);
            throw error;
        }
    }

    /**
     * Check if a document exists
     */
    async exists(id: string): Promise<boolean> {
        this.logOperation('exists', id);

        try {
            const doc = await this.getDocumentRef(id).get();
            const result = doc.exists;

            this.logSuccess('exists', id, { exists: result });
            return result;
        } catch (error) {
            this.logError('exists', id, error as Error);
            throw error;
        }
    }

    /**
     * Batch create multiple documents
     * @returns the list of ids of the created documents
     */
    async batchCreate(entities: Array<{ id?: string; data: TCreate }>): Promise<string[]> {
        this.logOperation('batchCreate', 'batch', { count: entities.length });

        try {
            const batch = this.firestore.batch();
            const ids: string[] = [];

            entities.forEach(entity => {
                const docId = entity.id
                const docRef = !!docId ?
                    this.getCollection().doc(docId)
                    : this.getCollection().doc();
                batch.set(docRef, entity.data as any);
                ids.push(docRef.id);
            });

            await batch.commit();

            this.logSuccess('batchCreate', 'batch', { count: ids.length, ids });
            return ids;
        } catch (error) {
            this.logError('batchCreate', 'batch', error as Error, { count: entities.length });
            throw error;
        }
    }

    /**
     * Batch update multiple documents
     */
    async batchUpdate(updates: Array<{ id: string; data: TUpdate }>): Promise<void> {
        this.logOperation('batchUpdate', 'batch', { count: updates.length });

        try {
            const batch = this.firestore.batch();

            updates.forEach(({ id, data }) => {
                batch.update(this.getDocumentRef(id), data as any);
            });

            await batch.commit();

            this.logSuccess('batchUpdate', 'batch', { count: updates.length });
        } catch (error) {
            this.logError('batchUpdate', 'batch', error as Error, { count: updates.length });
            throw error;
        }
    }

    /**
     * Batch merge multiple documents
     */
    async batchMerge(updates: Array<{ id: string; data: TUpdate }>): Promise<void> {
        this.logOperation('batchMerge', 'batch', { count: updates.length });

        try {
            const batch = this.firestore.batch();

            updates.forEach(({ id, data }) => {
                batch.set(this.getDocumentRef(id), data as any, { merge: true });
            });

            await batch.commit();

            this.logSuccess('batchMerge', 'batch', { count: updates.length });
        } catch (error) {
            this.logError('batchMerge', 'batch', error as Error, { count: updates.length });
            throw error;
        }
    }

    /**
     * Batch delete multiple documents
     */
    async batchDelete(ids: string[]): Promise<void> {
        this.logOperation('batchDelete', 'batch', { count: ids.length, ids });

        try {
            const batch = this.firestore.batch();

            ids.forEach(id => {
                batch.delete(this.getDocumentRef(id));
            });

            await batch.commit();

            this.logSuccess('batchDelete', 'batch', { count: ids.length });
        } catch (error) {
            this.logError('batchDelete', 'batch', error as Error, { count: ids.length, ids });
            throw error;
        }
    }

    /**
     * Query documents (helper for common queries)
     */
    protected async query(
        queryFn: (ref: FirestoreCollectionReference) => FirestoreQuery
    ): Promise<WithID<T>[]> {
        this.logOperation('query', 'custom');

        try {
            const query = queryFn(this.getCollection());
            const snapshot = await query.get();
            const result = FirestoreService.querySnapshotToEntities<T>(snapshot);

            this.logSuccess('query', 'custom', { count: result.length });
            return result;
        } catch (error) {
            this.logError('query', 'custom', error as Error);
            throw error;
        }
    }

    /**
     * Count documents in collection
     */
    // async count(): Promise<number> {
    //     const snapshot = await this.getCollection().count().get();
    //     return snapshot.data().count;
    // }

    /**
     * Get documents with pagination
     */
    async paginate(
        limit: number,
        queryFn?: (ref: FirestoreCollectionReference) => FirestoreQuery,
        startAfterDoc?: FirestoreDocumentSnapshot
    ): Promise<{ documents: WithID<T>[]; lastDoc: FirestoreDocumentSnapshot | null }> {
        this.logOperation('paginate', 'batch', { limit });

        try {
            let query = queryFn?.(this.getCollection()).limit(limit)
                ?? this.getCollection().limit(limit);

            if (startAfterDoc) {
                query = query.startAfter(startAfterDoc);
            }

            const snapshot = await query.get();
            const documents = FirestoreService.querySnapshotToEntities<T>(snapshot);
            const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;

            this.logSuccess('paginate', 'batch', { count: documents.length, hasMore: !!lastDoc });
            return { documents, lastDoc };
        } catch (error) {
            this.logError('paginate', 'batch', error as Error, { limit });
            throw error;
        }
    }
}

export abstract class BaseFirestoreService<T, TCreate = T, TUpdate = Partial<T>> extends FirestoreService<T, TCreate, TUpdate> {
    async get(id: string): Promise<T | null> {
        this.logOperation('get', id);

        try {
            const doc = await this.getDocumentRef(id).get();
            const data = BaseFirestoreService.documentToEntity<T>(doc);

            this.logSuccess('get', id, { found: data !== null });
            return data;
        } catch (error) {
            this.logError('get', id, error as Error);
            throw error;
        }
    }
    async create(data: TCreate, id?: string): Promise<string> {
        this.logOperation('create', id || 'auto', data);

        try {
            let docId: string;

            if (id) {
                await this.getDocumentRef(id).set(data as any);
                docId = id;
            } else {
                const docRef = await this.getCollection().add(data as any);
                docId = docRef.id;
            }

            this.logSuccess('create', docId, { data });
            return docId;
        } catch (error) {
            this.logError('create', id || 'auto', error as Error, data);
            throw error;
        }
    }
    async update(id: string, data: TUpdate): Promise<void> {
        this.logOperation('update', id, data);

        try {
            await this.getDocumentRef(id).update(data as any);

            this.logSuccess('update', id, { data });
        } catch (error) {
            this.logError('update', id, error as Error, data);
            throw error;
        }
    }
    async delete(id: string): Promise<void> {
        this.logOperation('delete', id);

        try {
            await this.getDocumentRef(id).delete();

            this.logSuccess('delete', id);
        } catch (error) {
            this.logError('delete', id, error as Error);
            throw error;
        }
    }
    async merge(id: string, data: TUpdate): Promise<void> {
        this.logOperation('merge', id);

        try {
            await this.getDocumentRef(id).set(data as any, { merge: true });

            this.logSuccess('merge', id);
        } catch (error) {
            this.logError('merge', id, error as Error);
            throw error;
        }
    }

    /** 
     * List the documents
     * @param queryFnOrLimit the specific query, or the limit (number)
     */
    list(queryFnOrLimit?: ((ref: FirestoreCollectionReference) => FirestoreQuery) | number): Promise<WithID<T>[]> {
        if (!queryFnOrLimit) {
            return super.list();
        } else {
            if (typeof queryFnOrLimit === 'number') {
                return super.query((collection) => collection.limit(queryFnOrLimit))
            } else {
                return super.query(queryFnOrLimit)
            }
        }
    }
}

/** Specific Base Firestore Sarevice for data which include a timestamp */
export abstract class BaseFirestoreServiceWithTimestamp<T extends { timestamp: FirestoreTimestamp }> extends BaseFirestoreService<T> {

    create(data: Omit<T, 'timestamp'>, id?: string): Promise<string> {
        return super.create({
            ...data,
            timestamp: FirestoreTimestampServerValue
        } as T, id)
    }

    update(id: string, data: Partial<Omit<T, 'timestamp'>>): Promise<void> {
        return super.update(id, {
            ...data,
            timestamp: FirestoreTimestampServerValue
        } as T)
    }

    merge(id: string, data: Partial<Omit<T, 'timestamp'>>): Promise<void> {
        return super.merge(id, {
            ...data,
            timestamp: FirestoreTimestampServerValue
        } as T)
    }

    batchCreate(entities: { id?: string; data: Omit<T, 'timestamp'>; }[]): Promise<string[]> {
        return super.batchCreate(
            entities.map(entity => ({
                id: entity.id,
                data: {
                    ...entity.data,
                    timestamp: FirestoreTimestampServerValue
                } as T
            })))
    }

    batchUpdate(updates: { id: string; data: Partial<Omit<T, 'timestamp'>>; }[]): Promise<void> {
        return super.batchUpdate(
            updates.map(entity => ({
                id: entity.id,
                data: {
                    ...entity.data,
                    timestamp: FirestoreTimestampServerValue
                } as T
            }))
        )
    }

    batchMerge(updates: { id: string; data: Partial<T>; }[]): Promise<void> {
        return super.batchMerge(
            updates.map(entity => ({
                id: entity.id,
                data: {
                    ...entity.data,
                    timestamp: FirestoreTimestampServerValue
                }
            }))
        )
    }
}